import { useEffect, useState } from "react";
import { Calculator, MapPin, Loader2, Copy, Settings, Plus, Truck, Clock } from "lucide-react";
import { toast } from "sonner";
import AddressAutocomplete from "../components/AddressAutocomplete";
import ManualTowPricingDialog from "../components/ManualTowPricingDialog";
import NewJobSheet from "../components/NewJobSheet";
import { DateTime24 } from "../components/DateTime24";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { useAuth } from "../lib/auth";
import {
  quoteAaTow,
  quoteSnTow,
  quoteManualTow,
} from "../lib/api";
import { toTenantWall, wallToIso } from "../lib/tenantTime";
import { tenantLocale, tenantTz } from "../lib/company";

/**
 * Tow Calc — Oct 2026.
 *
 * Interactive quote tool for ad-hoc customer calls. Pick a basis
 * (AA / First Rescue / Manual), punch in pickup + drop-off, get an
 * instant road-km round-trip quote with full breakdown.
 */
const BASES = [
  { k: "manual", label: "Manual", color: "emerald" },
  { k: "aa", label: "AA", color: "blue" },
  { k: "fr", label: "First Rescue", color: "purple" },
];


const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function TowCalcPage() {
  const { user } = useAuth();
  const canCreateJob = ["admin", "dispatcher", "owner"].includes(user?.role);
  const [basis, setBasis] = useState("manual");
  const [pickup, setPickup] = useState({ label: "", lat: null, lng: null });
  const [drop, setDrop] = useState({ label: "", lat: null, lng: null });
  const [when, setWhen] = useState(() => toTenantWall());
  // Heavy vehicle / truck tick box — Feb 2026. Only affects the Manual
  // basis (AA/FR dictate their own rate cards; we don't invent surcharges
  // on their jobs). Flag flows through to the quote endpoint which adds
  // the configured `heavy_vehicle_surcharge` on top of everything else.
  const [heavy, setHeavy] = useState(false);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [rateBump, setRateBump] = useState(0); // bumps on save → re-quote
  // "Create as Job" opens the existing NewJobSheet with pickup/drop/
  // manual-charge pre-filled. Hidden for drivers (gated above).
  const [createJobOpen, setCreateJobOpen] = useState(false);

  const canQuote = pickup.lat && pickup.lng && drop.lat && drop.lng;

  // Auto re-quote on input change.
  useEffect(() => {
    if (!canQuote) {
      setQuote(null);
      setErr("");
      return;
    }
    setLoading(true);
    setErr("");
    const t = setTimeout(async () => {
      try {
        const iso = wallToIso(when);
        if (basis === "manual") {
          const r = await quoteManualTow({
            pickup_lat: pickup.lat,
            pickup_lng: pickup.lng,
            drop_lat: drop.lat,
            drop_lng: drop.lng,
            at: iso,
            heavy_vehicle: heavy,
          });
          setQuote({ basis: "manual", ...r });
        } else {
          // AA / FR take distance_km only — we need the round-trip from
          // depot. Call the manual quote first (zero cost since it's
          // just a routing call + we discard its pricing) to get the
          // km, then feed into AA/FR quote. Heavy flag is ignored on
          // provider bases — their rate cards don't include it.
          const route = await quoteManualTow({
            pickup_lat: pickup.lat,
            pickup_lng: pickup.lng,
            drop_lat: drop.lat,
            drop_lng: drop.lng,
            at: iso,
          });
          const providerFn = basis === "aa" ? quoteAaTow : quoteSnTow;
          const providerBody = {
            distance_km: route.distance_km,
            at: iso,
          };
          if (basis === "fr") providerBody.service_type = "tow";
          const price = await providerFn(providerBody);
          setQuote({
            basis,
            route,
            price,
          });
        }
      } catch (e) {
        setErr(e?.response?.data?.detail || "Quote failed");
        setQuote(null);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [basis, pickup.lat, pickup.lng, drop.lat, drop.lng, when, heavy, canQuote, rateBump]);

  const copyQuote = () => {
    if (!quote) return;
    const lines = buildQuoteText(quote);
    navigator.clipboard
      .writeText(lines)
      .then(() => toast.success("Quote copied to clipboard"))
      .catch(() => toast.error("Could not access clipboard"));
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-5" data-testid="tow-calc-page">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Calculator className="h-6 w-6 text-emerald-600" />
          <h1 className="text-2xl font-bold">Tow Calc</h1>
        </div>
        {basis === "manual" && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSettingsOpen(true)}
            data-testid="tow-calc-manual-settings-btn"
            title="Adjust manual tow rates"
          >
            <Settings className="h-4 w-4 mr-1.5" />
            Rates
          </Button>
        )}
      </div>
      <p className="text-sm text-slate-500 dark:text-slate-400 -mt-2">
        Price a tow on the fly using road km round-trip from your depot.
        {basis === "manual"
          ? " Tap Rates to adjust hourly, FAF and surcharges."
          : " AA & First Rescue rate cards live in Settings → Integrations."}
      </p>

      <ManualTowPricingDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        onSaved={() => setRateBump((n) => n + 1)}
      />

      {/* Basis tabs */}
      <div className="flex gap-2" data-testid="tow-calc-basis-tabs">
        {BASES.map((b) => (
          <button
            key={b.k}
            type="button"
            onClick={() => setBasis(b.k)}
            data-testid={`tow-calc-basis-${b.k}`}
            className={
              basis === b.k
                ? "px-4 py-2 rounded-md bg-slate-900 text-white font-semibold text-sm dark:bg-slate-100 dark:text-slate-900"
                : "px-4 py-2 rounded-md bg-slate-100 text-slate-600 font-semibold text-sm hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700"
            }
          >
            {b.label}
          </button>
        ))}
      </div>

      {/* Inputs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
        <div>
          <Label className="text-xs flex items-center gap-1">
            <MapPin className="h-3 w-3 text-emerald-600" /> Pickup
          </Label>
          <AddressAutocomplete
            value={pickup.label}
            onChange={(v) => setPickup({ ...pickup, label: v })}
            onPick={(p) => setPickup(p)}
            placeholder="Where are we picking up?"
            testIdPrefix="tow-calc-pickup"
          />
        </div>
        <div>
          <Label className="text-xs flex items-center gap-1">
            <MapPin className="h-3 w-3 text-red-600" /> Drop-off
          </Label>
          <AddressAutocomplete
            value={drop.label}
            onChange={(v) => setDrop({ ...drop, label: v })}
            onPick={(p) => setDrop(p)}
            placeholder="Where are we dropping off?"
            testIdPrefix="tow-calc-drop"
          />
        </div>
        <div>
          <Label htmlFor="tow-calc-when" className="text-xs flex items-center gap-1">
            <Clock className="h-3 w-3 text-slate-500" /> When
          </Label>
          <DateTime24
            value={when}
            onChange={setWhen}
            dataTestId="tow-calc-when"
          />
        </div>
        {/* Heavy vehicle / truck — presented as a proper input cell to
            match Pickup / Drop-off / When: same label chrome, same h-10
            boxed field, same font. Manual basis only; disabled + muted
            for AA / FR with an inline hint so dispatchers understand
            why it's inert rather than wondering if it's broken. */}
        <div>
          <Label htmlFor="tow-calc-heavy-checkbox" className="text-xs flex items-center gap-1">
            <Truck className="h-3 w-3 text-indigo-600" /> Vehicle size
          </Label>
          <label
            htmlFor="tow-calc-heavy-checkbox"
            className={
              "w-full h-10 px-3 rounded-md border bg-white dark:bg-slate-800 text-sm flex items-center gap-2 " +
              (basis === "manual"
                ? "border-slate-200 dark:border-slate-700 cursor-pointer text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/70"
                : "border-slate-200 dark:border-slate-700 cursor-not-allowed text-slate-400 dark:text-slate-500")
            }
            data-testid="tow-calc-heavy-label"
          >
            <input
              id="tow-calc-heavy-checkbox"
              type="checkbox"
              checked={heavy}
              disabled={basis !== "manual"}
              onChange={(e) => setHeavy(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
              data-testid="tow-calc-heavy-checkbox"
            />
            <span className="truncate" title="Heavy Vehicle/Motorhome/Caravan">Heavy Vehicle/Motorhome/Caravan</span>
            {basis !== "manual" && (
              <span className="ml-auto text-[11px] italic text-slate-400 shrink-0">Manual only</span>
            )}
          </label>
        </div>
      </div>

      {/* Quote card */}
      {err && (
        <div className="rounded-md border border-red-200 bg-red-50 text-red-800 p-3 text-sm dark:bg-red-950/40 dark:border-red-900/50 dark:text-red-200" data-testid="tow-calc-error">
          {err}
        </div>
      )}
      {loading && (
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Routing &amp; pricing…
        </div>
      )}
      {quote && !loading && (
        <QuoteCard
          quote={quote}
          basis={basis}
          onCopy={copyQuote}
          onCreateJob={canCreateJob ? () => setCreateJobOpen(true) : null}
        />
      )}

      {/* Create as Job sheet — opens the existing NewJobSheet with pickup
          / drop / manual tow charge pre-filled from the current quote so
          the dispatcher doesn't have to re-type everything. Only staff
          see the trigger button (drivers' Price Check doesn't render it). */}
      {canCreateJob && (
        <NewJobSheet
          open={createJobOpen}
          onOpenChange={setCreateJobOpen}
          prefill={quote ? {
            address: pickup.label,
            lat: pickup.lat,
            lng: pickup.lng,
            drop_off_address: drop.label,
            drop_off_lat: drop.lat,
            drop_off_lng: drop.lng,
            manual_tow_charge: quote.basis === "manual" ? quote.totals?.ex_gst : undefined,
            heavy_vehicle: heavy,
            // Drop the quoted price (+ provenance) into Notes so the
            // dispatcher has an audit trail — "we quoted $X on Y
            // basis" — even if the manual charge is later edited.
            notes: buildQuoteNote(quote, basis, heavy),
          } : null}
        />
      )}
    </div>
  );
}

function QuoteCard({ quote, basis, onCopy, onCreateJob }) {
  const isManual = quote.basis === "manual";
  const route = isManual ? quote : quote.route;
  const price = isManual ? null : quote.price;
  const totals = isManual
    ? quote.totals
    : {
        ex_gst: price?.total ?? 0,
        gst_rate: 0.15,
        gst_amount: Number(((price?.total ?? 0) * 0.15).toFixed(2)),
        inc_gst: Number(((price?.total ?? 0) * 1.15).toFixed(2)),
      };

  return (
    <div
      className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 space-y-4"
      data-testid="tow-calc-quote"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">
          Quote · <span className="text-emerald-600 uppercase text-sm font-bold tracking-wide">{basis}</span>
        </h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onCopy} data-testid="tow-calc-copy-btn">
            <Copy className="h-3.5 w-3.5 mr-1.5" />
            Copy
          </Button>
          {onCreateJob && (
            <Button
              size="sm"
              onClick={onCreateJob}
              data-testid="tow-calc-create-job-btn"
              className="bg-slate-900 hover:bg-slate-800 text-white dark:bg-slate-100 dark:hover:bg-slate-200 dark:text-slate-900"
              title="Open New Job with pickup, drop-off and quoted charge pre-filled"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Create as Job
            </Button>
          )}
        </div>
      </div>

      {/* Routing summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <Fact label="Distance (round-trip)" value={`${route.distance_km} km`} />
        <Fact
          label="Drive time"
          value={fmtMins(route.drive_minutes)}
          hint={
            route.travel_time_correction_pct > 0
              ? `${fmtMins(route.drive_minutes_raw)} route + ${route.travel_time_correction_pct}% correction`
              : null
          }
        />
        {isManual && (
          <>
            <Fact label="Load/unload" value={`${route.load_unload_minutes} min`} />
            <Fact
              label="Billable time"
              value={fmtMins(route.billable_minutes)}
              hint={
                route.bill_time_rounding_minutes > 0
                  ? `rounded to ${route.bill_time_rounding_minutes} min blocks`
                  : null
              }
            />
          </>
        )}
        <Fact label="After hours?" value={route.is_after_hours ? "Yes" : "No"} />
      </div>

      {/* Breakdown */}
      <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Breakdown</div>
        {isManual ? (
          <ManualBreakdown b={route.breakdown} />
        ) : (
          <ProviderBreakdown b={price?.breakdown} />
        )}
      </div>

      {/* Totals */}
      <div className="border-t border-slate-200 dark:border-slate-700 pt-4 space-y-1">
        <div className="flex justify-between text-sm">
          <span className="text-slate-600 dark:text-slate-400">Total (ex-GST)</span>
          <span className="font-mono font-semibold">{fmt(totals.ex_gst)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-600 dark:text-slate-400">
            GST ({Math.round((totals.gst_rate || 0.15) * 100)}%)
          </span>
          <span className="font-mono">{fmt(totals.gst_amount)}</span>
        </div>
        <div className="flex justify-between text-lg pt-1 border-t border-slate-100 dark:border-slate-800">
          <span className="font-bold">Total (inc-GST)</span>
          <span className="font-mono font-bold text-emerald-600" data-testid="tow-calc-total-inc-gst">
            {fmt(totals.inc_gst)}
          </span>
        </div>
      </div>
    </div>
  );
}

function Fact({ label, value, hint }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="font-semibold text-slate-800 dark:text-slate-100">{value}</div>
      {hint && <div className="text-[11px] text-slate-400 mt-0.5">{hint}</div>}
    </div>
  );
}

function ManualBreakdown({ b }) {
  if (!b) return null;
  return (
    <div className="space-y-1 text-sm font-mono">
      <Row k="Time charge" v={fmt(b.time_charge)} hint={`@ $${b.hourly_rate}/hr`} />
      {b.km_charge > 0 && <Row k="Per-km" v={fmt(b.km_charge)} hint={`@ $${b.per_km_rate}/km`} />}
      <Row k="FAF" v={fmt(b.faf_charge)} hint={`@ $${b.faf_per_km}/km`} />
      {b.hit_minimum && (
        <Row k="Minimum charge applied" v={fmt(b.minimum_charge)} hint="1h floor" />
      )}
      {b.after_hours_surcharge > 0 && (
        <Row k="After-hours surcharge" v={fmt(b.after_hours_surcharge)} hint="on top of minimum" />
      )}
      {b.heavy_vehicle_surcharge > 0 && (
        <Row k="Heavy vehicle surcharge" v={fmt(b.heavy_vehicle_surcharge)} hint="on top of minimum" />
      )}
    </div>
  );
}

function ProviderBreakdown({ b }) {
  if (!b) return null;
  return (
    <div className="space-y-1 text-sm font-mono">
      <Row k="Base tow" v={fmt(b.base_tow)} />
      <Row k={`Extra km (${b.extra_km})`} v={fmt(b.extra_km_charge)} hint={`@ $${b.per_km_rate}/km`} />
      {b.after_hours_surcharge > 0 && (
        <Row k="After-hours surcharge" v={fmt(b.after_hours_surcharge)} />
      )}
      {b.faf_charge > 0 && <Row k="FAF" v={fmt(b.faf_charge)} />}
      {b.public_holiday_surcharge > 0 && (
        <Row k="Public holiday surcharge" v={fmt(b.public_holiday_surcharge)} />
      )}
    </div>
  );
}

function Row({ k, v, hint }) {
  return (
    <div className="flex justify-between">
      <span className="text-slate-600 dark:text-slate-400">
        {k}
        {hint && <span className="ml-1.5 text-[11px] text-slate-400">({hint})</span>}
      </span>
      <span>{v}</span>
    </div>
  );
}

const fmtMins = (m) => {
  // Round the TOTAL first. Splitting hours off the unrounded value and then
  // rounding the remainder gave "3h 60m" for 239.6 minutes (Jun 2026).
  const n = Math.round(Number(m || 0));
  if (n < 60) return `${n} min`;
  const h = Math.floor(n / 60);
  const r = n % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
};

const buildQuoteText = (q) => {
  const route = q.basis === "manual" ? q : q.route;
  const totals = q.basis === "manual"
    ? q.totals
    : {
        ex_gst: q.price?.total ?? 0,
        inc_gst: Number(((q.price?.total ?? 0) * 1.15).toFixed(2)),
      };
  return [
    `Tow quote (${q.basis.toUpperCase()})`,
    `Distance: ${route.distance_km} km`,
    `Drive time: ${fmtMins(route.drive_minutes)}`,
    q.basis === "manual" && route.billable_minutes
      ? `Billable time: ${fmtMins(route.billable_minutes)} (incl. ${route.load_unload_minutes} min load/unload)`
      : null,
    route.is_after_hours ? "After-hours tow" : "Business-hours tow",
    `Total (ex-GST): ${fmt(totals.ex_gst)}`,
    `Total (inc-GST): ${fmt(totals.inc_gst)}`,
  ].filter(Boolean).join("\n");
};

// Short, Notes-friendly quote summary stamped onto a job created from
// Tow Calc. Different shape to `buildQuoteText` (which copies to the
// customer's clipboard) — this one is terser so it doesn't dominate
// the Notes field and includes the heavy-vehicle flag so a dispatcher
// scanning History can see what basis the price was built on.
const buildQuoteNote = (q, basis, heavy) => {
  const route = q.basis === "manual" ? q : q.route;
  const totals = q.basis === "manual"
    ? q.totals
    : {
        ex_gst: q.price?.total ?? 0,
        inc_gst: Number(((q.price?.total ?? 0) * 1.15).toFixed(2)),
      };
  const stamp = new Date().toLocaleString(tenantLocale(), { timeZone: tenantTz(),
    dateStyle: "short",
    timeStyle: "short",
    hourCycle: "h23",
  });
  const bits = [
    `Tow Calc quote (${(basis || q.basis || "manual").toUpperCase()}) · ${stamp}`,
    `${route.distance_km} km round-trip · ${route.is_after_hours ? "after-hours" : "business-hours"}`,
    heavy ? "Heavy Vehicle/Motorhome/Caravan · surcharge applied" : null,
    `Quoted: ${fmt(totals.ex_gst)} ex-GST · ${fmt(totals.inc_gst)} inc-GST`,
  ].filter(Boolean);
  return bits.join("\n");
};
